/**
 * D17 / I114 — the ONE gate every drive-side surface (drive, mobile, the workspace trees) runs
 * before opening a `.chain`: a chain that Needs approval doesn't open, and its target shows only in
 * the Approve dialog. Whoever may approve it gets `ApprovePointerDialog`, anyone else
 * `PointerNeedsApprovalDialog`. The share view has no Approve and uses the reason dialog directly.
 *
 * `usePendingPointerGate` owns both dialogs and the approve call; `block(file)` answers true when it
 * handled the open (the caller then stops). Deep trees read `block` from `PendingPointerGateContext`
 * instead of threading a prop through every level.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@papan/i18n'
import { isPendingPointer, type PendingPointerLike } from './pending-pointer.js'
import { useToast } from '@papan/core-ui/ui'
import { filesApi } from '@papan/files/client'
import { ApprovePointerDialog } from './ApprovePointerDialog.js'
import { PointerNeedsApprovalDialog } from './PointerNeedsApprovalDialog.js'

export { isPendingPointer, type PendingPointerLike } from './pending-pointer.js'

export function usePendingPointerGate({ urlPrefix, rootLabel, onNavigate, onApproved }: {
  /** `/~` or `/t/<tenantId>` — where the drive's folder / app routes live. */
  urlPrefix: string
  /** The drive root's label, as that surface's breadcrumb shows it. */
  rootLabel: string
  /** Open a folder (null = the drive root). Defaults to the drive's folder route. */
  onNavigate?: (folderId: string | null) => void
  /** After a successful Approve — refresh the surface's listing. */
  onApproved?: (file: PendingPointerLike) => void
}): { block: (f: PendingPointerLike) => boolean; openApprove: (f: PendingPointerLike) => void; dialogs: ReactNode } {
  const { t } = useTranslation('files')
  const toast = useToast()
  const routerNavigate = useNavigate()
  const [approving, setApproving] = useState<PendingPointerLike | null>(null)
  const [info, setInfo] = useState<PendingPointerLike | null>(null)

  const block = useCallback((f: PendingPointerLike) => {
    if (!isPendingPointer(f)) return false
    if (f.chainCanApprove) setApproving(f)
    else setInfo(f)
    return true
  }, [])

  const navigate = useCallback((folderId: string | null) => {
    if (onNavigate) onNavigate(folderId)
    else routerNavigate(folderId ? `${urlPrefix}/folder/${folderId}` : `${urlPrefix}/folder`)
  }, [onNavigate, routerNavigate, urlPrefix])

  const approve = useCallback(async (f: PendingPointerLike) => {
    try {
      await filesApi.approvePointer(f.id)
      toast.success(t('driveView.chain.approved', { name: f.name }))
      onApproved?.(f)
    } catch {
      toast.error(t('driveView.chain.approveFailed'))
    }
  }, [onApproved, t, toast])

  const dialogs = (
    <>
      {approving && (
        <ApprovePointerDialog
          fileId={approving.id}
          fileName={approving.name}
          rootLabel={rootLabel}
          onNavigate={navigate}
          appHref={(appId) => `${urlPrefix}/app/${appId}`}
          onOpenApp={(appId) => routerNavigate(`${urlPrefix}/app/${appId}`)}
          onCancel={() => setApproving(null)}
          onApprove={async () => { await approve(approving); setApproving(null) }}
        />
      )}
      {info && <PointerNeedsApprovalDialog fileName={info.name} isFolder={info.isFolder ?? true} onClose={() => setInfo(null)} />}
    </>
  )
  return { block, openApprove: setApproving, dialogs }
}

const PendingPointerGateContext = createContext<((f: PendingPointerLike) => boolean) | null>(null)

export function PendingPointerGateProvider({ block, children }: { block: (f: PendingPointerLike) => boolean; children: ReactNode }) {
  return <PendingPointerGateContext.Provider value={block}>{children}</PendingPointerGateContext.Provider>
}

/** The surrounding gate's `block`; with no provider nothing is blocked here (the server decides). */
export function usePendingPointerBlock(): (f: PendingPointerLike) => boolean {
  const ctx = useContext(PendingPointerGateContext)
  return useMemo(() => ctx ?? (() => false), [ctx])
}
